import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  finalize,
  Observable,
  of,
  switchMap,
  firstValueFrom
} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Token } from '../../models/token';
import { JwtHelperService } from '@auth0/angular-jwt';
import { User } from '../../models/user';
import { FormioToken } from '../../models/formio-token';
import { isSameOriginUrl, routerUrlToAbsolute } from '../../utils/url-utils';

type TokenResponse = {
  access_token: string;
  refresh_token: string;
};

type AuthCheckResponse = {
  status: boolean;
};

@Injectable()
export class CasService {
  private jwt: string | null;
  private refreshToken: string | null;
  private roleCache: any = {};
  private processedAuthCode: boolean = false;
  private readonly postLoginRedirectKey = 'postLoginRedirect';
  // This will reach the outside world
  private readonly jwtSubject = new BehaviorSubject<string | null>(null);
  jwt$ = this.jwtSubject.asObservable();
  // This we use to internally reacting to finding something in local storage
  private readonly internalJwtSubject = new BehaviorSubject<string | null>(
    null
  );
  private readonly internalJwt$ = this.internalJwtSubject.asObservable();
  updateTokenSubject = new BehaviorSubject<string | null>(null);
  // Outside world can request refresh
  updateToken$ = this.updateTokenSubject.asObservable();
  private readonly jwtHelperService: JwtHelperService = new JwtHelperService();
  private readonly defaultCasHeaders = {
    headers: {
      Authorization: `Basic ${btoa(
        environment.OAUTH_CLIENT_ID + ':' + environment.OAUTH_SECRET
      )}`,
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  };

  constructor(
    @Inject(LOCALE_ID) private readonly locale: string,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly http: HttpClient
  ) {
    this.refreshToken =
      localStorage.getItem(Token.refreshTokenNameInStorage) ?? null;
    if (environment.DEBUG_MODE) {
      console.log(
        `Refresh token loaded from local storage: ${this.refreshToken}`
      );
    }

    this.updateToken$
      .pipe(
        filter((refreshToken) => refreshToken !== null),
        switchMap((refreshToken) => {
          if (environment.DEBUG_MODE) {
            console.log(
              `Token refresh requested; using refresh token to refresh`
            );
          }

          return this.updateToken(refreshToken);
        })
      )
      .subscribe(() => {
        // Handle the result of token refresh if needed
      });

    this.internalJwt$
      .pipe(
        filter((jwt) => jwt !== null),
        distinctUntilChanged(),
        switchMap((jwt) => {
          if (environment.DEBUG_MODE) {
            console.log(`CAS service internal JWT changed to: ${jwt}`);
          }

          return this.verifyJwtAgainstBackend(jwt!);
        })
      )
      .subscribe((authenticated) => {
        if (authenticated) {
          // This is a fine JWT
          this.jwtSubject.next(this.jwt);

          if (!this.jwt || !this.refreshToken) {
            if (environment.DEBUG_MODE) {
              console.warn('Why are these tokens empty?');
            }
          } else {
            localStorage.setItem(Token.tokenNameInStorage, this.jwt);
            localStorage.setItem(
              FormioToken.tokenNameInStorage,
              FormioToken.scheme + this.jwt
            );
            localStorage.setItem(
              Token.refreshTokenNameInStorage,
              this.refreshToken
            );
          }
        } else {
          this.jwt = null;
          this.jwtSubject.next(null);
          localStorage.removeItem(Token.tokenNameInStorage);
          localStorage.removeItem(FormioToken.tokenNameInStorage);
          this.updateTokenSubject.next(this.refreshToken);
        }
      });

    this.route.queryParams
      .pipe(
        switchMap((params: any) => {
          const returnedState = params['state'] || null;
          let expectedState: string | null = null;
          try {
            expectedState = sessionStorage.getItem('casAuthState');
          } catch (e) {
            // ignore
          }

          if (
            params['code'] &&
            !this.processedAuthCode &&
            returnedState &&
            expectedState &&
            returnedState === expectedState
          ) {
            this.processedAuthCode = true;
            if (environment.DEBUG_MODE) {
              console.log(
                `'code' detected among GET params with matching CAS state: ${params['code']}`
              );
            }

            return this.exchangeCodeForTokens(params['code']);
          }

          return of(null);
        })
      )
      .subscribe(() => {
        // Handle the result of the exchangeCodeForTokens if needed
      });

    // Fallback: detect code directly from URL if ActivatedRoute is not yet ready
    try {
      const url = new URL(window.location.href);
      const directCode = url.searchParams.get('code');
      const returnedState = url.searchParams.get('state');
      let expectedState: string | null = null;
      try {
        expectedState = sessionStorage.getItem('casAuthState');
      } catch (e) {
        // ignore
      }
      if (
        directCode &&
        !this.processedAuthCode &&
        returnedState &&
        expectedState &&
        returnedState === expectedState
      ) {
        if (environment.DEBUG_MODE) {
          console.log(
            `'code' detected directly in URL with matching CAS state: ${directCode}`
          );
        }
        this.processedAuthCode = true;
        this.exchangeCodeForTokens(directCode).subscribe();
      }
    } catch (e) {
      // ignore URL parsing errors
    }

    this.jwt = localStorage.getItem(Token.tokenNameInStorage) ?? null;
    if (environment.DEBUG_MODE) {
      console.log(`JWT loaded from local storage: ${this.jwt}`);
    }
    if (this.jwt) {
      setTimeout(() => this.internalJwtSubject.next(this.jwt), 0);
    }
  }

  // Early, idempotent processing of CAS authorization code before app bootstrap
  public processAuthCodeFromUrlEarly(): Promise<void> {
    if (this.processedAuthCode) {
      return Promise.resolve();
    }

    let code: string | null = null;
    try {
      code = new URL(window.location.href).searchParams.get('code');
    } catch (e) {
      // ignore URL parsing errors
    }

    if (!code) {
      // If there is no code in URL early on, redirect to CAS login preserving full path and params
      try {
        const pathWithParams = `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`;
        this.login(false, pathWithParams);
      } catch (e) {
        // Fallback to simple login without explicit path
        this.login(false);
      }
      return Promise.resolve();
    }

    this.processedAuthCode = true;
    return firstValueFrom(
      this.exchangeCodeForTokens(code).pipe(
        catchError((err) => {
          if (environment.DEBUG_MODE) {
            console.warn('Early code exchange failed:', err);
          }
          return of(null);
        })
      )
    ).then(() => void 0);
  }

  private _buildRedirectUri(pathOverride?: string): string {
    try {
      const base = (environment.OAUTH_REDIRECT_URL ?? '').replace(/\/+$/, '');
      let path =
        pathOverride ?? (this.router.url ? this.router.url.split('?')[0] : '/');
      if (!path || path === '/') {
        return environment.OAUTH_REDIRECT_URL;
      }
      if (!path.startsWith('/')) {
        path = '/' + path;
      }
      return `${base}${path}`;
    } catch (e) {
      return environment.OAUTH_REDIRECT_URL;
    }
  }

  login(isRegister: boolean = false, redirectPath?: string) {
    const randomChoices =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const randomId = Array.from(
      { length: 15 },
      () => randomChoices[Math.floor(Math.random() * randomChoices.length)]
    ).join('');

    // Persist CAS state to validate return and avoid processing foreign 'code' parameters
    try {
      sessionStorage.setItem('casAuthState', randomId);
    } catch (e) {
      // ignore storage errors
    }

    // Preserve full target (including query and hash) for post-login restoration
    const targetFull = redirectPath ?? this.router.url;
    try {
      if (targetFull) {
        sessionStorage.setItem(this.postLoginRedirectKey, targetFull);
      }
    } catch (e) {
      // ignore storage errors
    }

    // Build redirect URI with path only (no query/hash) to keep OAuth redirect stable
    let pathOnly: string | undefined = undefined;
    if (targetFull) {
      try {
        const url = new URL(targetFull, window.location.origin);
        pathOnly = url.pathname || undefined;
      } catch (e) {
        pathOnly = targetFull.split('?')[0].split('#')[0];
        if (!pathOnly) {
          pathOnly = undefined;
        }
      }
    }

    const redirectUri = this._buildRedirectUri(pathOnly);
    let redirectLink = `${environment.CAS_URL}cas/spa.html#/?response_type=code&client_id=${environment.OAUTH_CLIENT_ID}&state=${randomId}&scope=any&redirect_uri=${encodeURIComponent(redirectUri)}&lang=${this.locale}`;
    if (isRegister) {
      redirectLink = redirectLink.split('#/').join('#/sign-up');
    }

    window.location.href = redirectLink;
  }

  private _reactToLogout() {
    localStorage.removeItem(Token.tokenNameInStorage);
    localStorage.removeItem(FormioToken.tokenNameInStorage);
    localStorage.removeItem(Token.refreshTokenNameInStorage);
    this.jwt = null;
    this.jwtSubject.next(null);
    this.refreshToken = null;
    this.login(false);
  }

  logout() {
    const token = this.jwt ?? localStorage.getItem(Token.tokenNameInStorage);
    const options = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    return this.http
      .get(`${environment.CAS_URL}userdata/invalidateToken`, options)
      .pipe(
        finalize(() => {
          // This code block will be executed regardless of success or failure
          this._reactToLogout();
        })
      );
  }

  loadUserProfile(token: string): User {
    const decoded = this.jwtHelperService.decodeToken(token);
    const user = new User();
    user.first_name = decoded.fname;
    user.last_name = decoded.lname;
    user.email = decoded.email;
    user.emailVerified = decoded.email_verified;

    return user;
  }

  private _saveTokenResponse(response: TokenResponse) {
    this.jwt = response.access_token;
    this.jwtSubject.next(this.jwt);
    this.refreshToken = response.refresh_token;
    localStorage.setItem(Token.tokenNameInStorage, this.jwt);
    localStorage.setItem(
      FormioToken.tokenNameInStorage,
      FormioToken.scheme + this.jwt
    );
    localStorage.setItem(Token.refreshTokenNameInStorage, this.refreshToken);
  }

  private exchangeCodeForTokens(code: string): Observable<TokenResponse> {
    const body = new URLSearchParams();
    body.set('code', code);
    body.set('redirect_uri', this._buildRedirectUri());
    body.set('grant_type', 'authorization_code');

    return this.http
      .post<TokenResponse>(
        `${environment.CAS_URL}access_token`,
        body.toString(),
        this.defaultCasHeaders
      )
      .pipe(
        map((response: TokenResponse) => {
          this._saveTokenResponse(response);
          // Clear CAS state on successful exchange
          try {
            sessionStorage.removeItem('casAuthState');
          } catch (e) {
            // ignore
          }
          // Decide where to land: prefer a previously saved target (with params), otherwise just remove code/state
          let finalUrl: string | null = null;
          try {
            const saved = sessionStorage.getItem(this.postLoginRedirectKey);
            if (saved) {
              if (saved.startsWith('http')) {
                if (isSameOriginUrl(saved)) {
                  finalUrl = saved;
                }
              } else {
                finalUrl = routerUrlToAbsolute(saved);
              }
              sessionStorage.removeItem(this.postLoginRedirectKey);
            }
          } catch (e) {
            // ignore storage errors
          }

          if (!finalUrl) {
            const currentUrl = window.location.href;
            finalUrl = currentUrl.replace(/[?&](code|state)=[^&]+/g, '');
          }

          const currentUrlNow = window.location.href;
          if (currentUrlNow !== finalUrl) {
            window.history.replaceState({}, document.title, finalUrl);
          }

          return response;
        })
      );
  }

  private verifyJwtAgainstBackend(jwt: string): Observable<boolean> {
    // None of the in-browser libraries allow for a proper public key based JWT verification, so use the server
    // JwtModule will attach Bearer token automatically
    const authHeader = 'Bearer ' + jwt;
    const headers = new HttpHeaders({
      Authorization: authHeader
    });

    return this.http
      .get<AuthCheckResponse>(`${environment.BASE_URL}site-auth-check`, {
        headers: headers
      })
      .pipe(
        map((response: AuthCheckResponse) => {
          return response.status;
        })
      );
  }

  private updateToken(refreshToken: string | null): Observable<TokenResponse> {
    if (refreshToken === null) {
      // Fallback to localStorage if no refresh token provided
      refreshToken =
        localStorage.getItem(Token.refreshTokenNameInStorage) ?? '';
    }
    const body = new URLSearchParams();
    body.set('refresh_token', refreshToken);
    body.set('redirect_uri', this._buildRedirectUri());
    body.set('grant_type', 'refresh_token');

    return this.http
      .post<TokenResponse>(
        `${environment.CAS_URL}access_token`,
        body.toString(),
        this.defaultCasHeaders
      )
      .pipe(
        map((response: TokenResponse) => {
          if (environment.DEBUG_MODE) {
            console.log(
              `Token refresh successful, got response: ${JSON.stringify(
                response
              )}`
            );
          }
          this._saveTokenResponse(response);

          return response;
        }),
        catchError((error) => {
          if (environment.DEBUG_MODE) {
            console.log(
              `Token refresh failed with error: ${JSON.stringify(error)}`
            );
          }

          this._reactToLogout();

          throw error;
        })
      );
  }

  isUserInRole(role: string): Observable<boolean> {
    // Check role cache first to avoid redundant API calls
    if (Object.prototype.hasOwnProperty.call(this.roleCache, role)) {
      return new Observable<boolean>((observer) => {
        // eslint-disable-next-line security/detect-object-injection -- role validated by hasOwnProperty check
        observer.next(this.roleCache[role]);
        observer.complete();
      });
    } else {
      return this.http.get<User>(`${environment.PARTC_URL}user/`).pipe(
        map((user: User) => {
          if (user) {
            user.roles.forEach((userRole: string) => {
              // eslint-disable-next-line security/detect-object-injection -- userRole from API response
              this.roleCache[userRole] = true;
            });

            return role in this.roleCache;
          } else {
            return false;
          }
        }),
        catchError((error: HttpErrorResponse) => {
          if (environment.DEBUG_MODE) {
            console.log(
              `Failed to check if user is in role ${role}: ${error.error?.message ?? error.message}`
            );
          }

          return of(false);
        })
      );
    }
  }
}
